import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Filters from "./Filters";
import PageControl from "../../components/PageControl";
import * as serviceActions from "../../actions/services";
import useMobileScreen from "../../hooks/useMobileScreen";
import { Client } from "../../interfaces/ClientInterface";
import { notificationAdd } from "../../actions/notification";
import OpdRequestList from "../Assessment/OpdRequest/OpdRequestList";
import { getOpdRequests, RequestForm } from "../../api/requestForms";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import OpdRequestInfo from "../Assessment/OpdRequest/OpdRequestInfo";
import { RootState } from "../../store";
import { CalendarFunctions } from "../../components/Calendar";

// css
import "./Reports.scss";

const pageSize = 20;

const LabReferralReport = (): React.ReactNode => {
  // hooks
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  // selectors
  const services = useSelector((state: RootState) => state.services.collection);
  const { mode, userCreds } = useSelector((state: RootState) => state.userContext);

  // variables
  const serviceProviderId = mode === "serviceProvider" ? userCreds.authenticableId : null;

  // states
  const [page, setPage] = React.useState(0);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [selectedClient, setSelectedClient] = useState<Partial<Client> | null>(null);
  const [selectedRow, setSelectedRow] = useState<Partial<RequestForm> | null>(null);

  const [filters, setFilters] = useState({
    from: CalendarFunctions.startOfDay(new Date()).toDate(),
    until: new Date(),
    clientIds: [],
    serviceProviderIds: serviceProviderId ? [serviceProviderId] : []
  });

  const [opdRequestData, setOpdRequestData] = useState<{ results: RequestForm[]; total: number }>({
    results: [],
    total: 0
  });

  // effects
  useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const response = await getOpdRequests({ page, pageSize, ...filters });
        setOpdRequestData(response);
      } catch (err) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: "Failed to get request forms",
            autoTimeout: true
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [page, dispatch, selectedClient?.id, filters]);

  React.useEffect(() => {
    if (!services || !services.length) {
      dispatch(serviceActions.getServices());
    }
  }, [dispatch, services]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* filter */}
      <Box sx={{ px: 3, py: 2 }}>
        <Filters
          filters={filters}
          onSetFilters={(newfilters) => {
            setFilters(newfilters);
            setPage(0);
          }}
          isRequestLoading={isRequestLoading}
        />
      </Box>

      {/* list */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflow: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1000px" : "auto", mb: "40pxs" }}>
          <OpdRequestList
            page={page}
            setPage={setPage}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            opdRequestData={opdRequestData}
            isListForReport
          />

          <Box className="labNavigatePage">
            <PageControl
              page={page}
              pageSize={pageSize}
              onSetPage={(v) => {
                setPage(v);
              }}
              maximumDataCount={opdRequestData.total}
            />
          </Box>
        </Box>
      </Box>

      {/* panels and dialogs */}
      {selectedRow && (
        <OpdRequestInfo
          onClose={() => setSelectedRow(null)}
          selectedRow={selectedRow}
          opdRequestData={opdRequestData}
          setOpdRequestData={setOpdRequestData}
          isInfoRenderedInReport
        />
      )}
    </Box>
  );
};

export default LabReferralReport;
